// Microphone level and the client energy VAD (moved from the legacy pcm.ts).
//
// The worklet posts one RMS value per 100 ms frame. The UI turns it into the
// smoothed 0..1 waveform level; the dictation safety stop uses the same curve,
// unsmoothed, against the existing 0.12 voice-activity threshold. Neither value
// is ever uploaded.

const AUDIO_LEVEL_NOISE_FLOOR = 0.008;
/** The legacy dictation VAD threshold on the unsmoothed level (the removed pre-BUILD 1 dictation adapter). */
export const VOICE_ACTIVITY_LEVEL = 0.12;

/** Root mean square of float samples in [-1, 1]. */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sumSquares = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index] ?? 0;
    sumSquares += sample * sample;
  }
  return Math.sqrt(sumSquares / samples.length);
}

const responsiveLevel = (value: number) =>
  Math.min(1, Math.sqrt(Math.max(0, (value - AUDIO_LEVEL_NOISE_FLOOR) * 5.5)));

/** Smoothed 0..1 level from one frame's RMS, for the waveform. */
export function levelFromRms(value: number, previous = 0): number {
  const responsive = responsiveLevel(value);
  const smoothing = responsive > previous ? 0.42 : 0.18;
  return Math.max(0, Math.min(1, previous + (responsive - previous) * smoothing));
}

/** Smoothed 0..1 level of raw samples (the legacy `measureAudioLevel`). */
export function measureAudioLevel(samples: Float32Array, previous = 0): number {
  if (samples.length === 0) return Math.max(0, previous * 0.72);
  return levelFromRms(rms(samples), previous);
}

/** True when one frame's RMS counts as speech for the safety stop. */
export const isVoiceActivity = (frameRms: number, threshold = VOICE_ACTIVITY_LEVEL): boolean =>
  levelFromRms(frameRms, 0) >= threshold;
